import { useEffect, useState, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CalendarDays, FileSignature, User, LogOut, Download, ChevronRight, HeartPulse, MessageCircle, Package, Receipt, Stethoscope } from "lucide-react";
import { MessageThread } from "../../components/MessageThread";
import { ContactButtons } from "../../components/ContactButtons";
import { PassportTab } from "./Passport";
import { api, API, errMsg } from "../../lib/api";
import { useT } from "../../lib/i18n";
import { money } from "../../lib/helpers";
import { StatusBadge } from "../../components/Common";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import PublicFrame, { usePublicBranding } from "../public/PublicFrame";
import { PackageList } from "../../components/ClientPackages";
import { IosInstallTip } from "../../components/IosInstallTip";

function PackagesTab({ slug }) {
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/portal/${slug}/packages`).then((r) => setD(r.data)); }, [slug]);
  if (!d) return null;
  return (
    <div className="space-y-3" data-testid="portal-packages">
      {d.codes.map((c) => <div key={c.id} className="rounded-[var(--radius)] border border-primary/30 bg-primary/5 p-4 text-sm" data-testid={`portal-code-${c.code}`}>{c.kind === "welcome" ? "Your welcome offer" : "Your birthday treat"}: <b>{c.percent}% off</b> with code <b>{c.code}</b> · until {c.expires_at.slice(0, 10)}
        <a href={`/book/${slug}?code=${c.code}`} className="ml-2 font-semibold text-primary" data-testid={`portal-code-use-${c.code}`}>Use now →</a></div>)}
      {d.gift_cards.map((g) => <div key={g.id} className="rounded-[var(--radius)] border bg-card p-4 text-sm flex flex-wrap items-center gap-2" data-testid={`portal-gift-${g.code}`}>
        <span className="flex-1 min-w-[180px]">Gift card <b>{g.code}</b>: <b data-testid={`portal-gift-balance-${g.code}`}>{g.balance.toFixed(2)} {g.currency}</b> left</span>
        <a href={`/book/${slug}?gift=${g.code}`}><Button size="sm" className="rounded-full" data-testid={`portal-gift-use-${g.code}`}>Use my gift card</Button></a>
        <a href={`/home/${slug}?gift=${g.code}`}><Button size="sm" variant="outline" className="rounded-full" data-testid={`portal-gift-home-${g.code}`}>Home visit</Button></a></div>)}
      <PackageList items={d.packages} />
      <a href={`/packages/${slug}`} className="inline-block text-sm font-semibold text-primary" data-testid="portal-buy-packages">Browse packages & gift cards →</a>
    </div>
  );
}

function LoyaltyCard({ slug }) {
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/portal/${slug}/loyalty`).then((r) => setD(r.data)).catch(() => {}); }, [slug]);
  if (!d?.enabled) return null;
  return (
    <div className="rounded-[var(--radius)] border bg-card p-4 mb-4" data-testid="portal-loyalty">
      <p className="text-sm font-semibold">Loyalty card · {d.stamps}/{d.visits}</p>
      <div className="flex flex-wrap gap-1.5 my-2">{Array.from({ length: d.visits }).map((_, i) => <span key={i} className={`h-7 w-7 rounded-full border-2 ${i < d.stamps ? "bg-primary border-primary" : "border-dashed"}`} />)}</div>
      <p className="text-xs text-muted-foreground">Complete {d.visits - d.stamps} more visit{d.visits - d.stamps === 1 ? "" : "s"} to get {d.percent}% off.</p>
    </div>
  );
}

function InvoicesTab({ slug }) {
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/portal/${slug}/invoices`).then((r) => setD(r.data)).catch(() => setD([])); }, [slug]);
  if (!d) return null;
  if (!d.length) return <p className="text-sm text-muted-foreground" data-testid="portal-invoices-empty">No invoices yet.</p>;
  return (
    <div className="space-y-2" data-testid="portal-invoices">
      {d.map((i) => (
        <div key={i.id} className="rounded-[var(--radius)] border bg-card p-4 flex items-center gap-3" data-testid={`portal-invoice-${i.number}`}>
          <Receipt className="h-4 w-4 text-primary shrink-0" />
          <div className="flex-1 min-w-0"><div className="font-medium text-sm">{i.kind === "credit_note" ? "Credit note" : "Invoice"} {i.number}</div>
            <div className="text-xs text-muted-foreground truncate">{i.date} · {i.lines?.[0]?.description}</div></div>
          <span className="text-sm font-semibold">{money(i.total, i.currency)}</span>
          <a href={`${API}/portal/${slug}/invoices/${i.id}/pdf`} target="_blank" rel="noreferrer" data-testid={`portal-invoice-pdf-${i.number}`}><Button size="sm" variant="outline" className="rounded-full"><Download className="h-3.5 w-3.5 mr-1" />PDF</Button></a>
        </div>
      ))}
    </div>
  );
}

function HealthTab({ slug }) {
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/portal/${slug}/health`).then((r) => setD(r.data)).catch(() => setD({})); }, [slug]);
  const open = () => api.post(`/portal/${slug}/health`).then((r) => { window.location.href = `/health/${r.data.token}`; }).catch((e) => toast.error(errMsg(e)));
  if (!d) return <p className="text-sm text-muted-foreground">…</p>;
  const text = d.valid ? `Completed on ${d.completed_at.slice(0, 10)} · valid until ${d.due_at.slice(0, 10)}` : d.completed_at ? "Your questionnaire is over 12 months old, please update it." : "Please complete your health questionnaire before your visit.";
  return (
    <div className="rounded-[var(--radius)] border bg-card p-5 space-y-3" data-testid="portal-health">
      <div className="flex items-center gap-2 font-semibold"><Stethoscope className="h-4 w-4 text-primary" />Health questionnaire</div>
      <p className="text-sm text-muted-foreground" data-testid="portal-health-status">{text}</p>
      {d.pending_token ? <a href={`/health/${d.pending_token}`}><Button className="rounded-full" data-testid="portal-health-fill-btn">Fill in questionnaire</Button></a>
        : <Button className="rounded-full" variant={d.valid ? "outline" : "default"} data-testid="portal-health-update-btn" onClick={open}>{d.valid ? "My health has changed: update" : "Fill in questionnaire"}</Button>}
    </div>
  );
}

export const L = {
  en: { title: "Your patient area", sub: "See your appointments, treatment notes and consent forms.", email: "Email", send: "Email me a login code",
    code: "6-digit code", verify: "Log in", usePw: "Use password instead", useCode: "Use email code instead", password: "Password",
    upcoming: "Upcoming", passport: "Passport", past: "Past", forms: "Forms", health: "Health", messages: "Messages", packages: "Packages", invoices: "Invoices", account: "Account", none: "Nothing here yet", notes: "Notes from your clinic",
    sign: "Sign now", setPw: "Set a password (optional)", save: "Save", install: "Install app", iosHint: "On iPhone: tap Share, then 'Add to Home Screen'.",
    book: "Book a new appointment", logout: "Log out", codeSent: "We sent a code to your email (if it's registered with this clinic).",
    create: "Create account", haveAccount: "Already have an account? Log in", noAccount: "New here? Create account", pwHint: "Password (min. 8 characters)", firstName: "First name", lastName: "Last name", phone: "Phone" },
  it: { title: "La tua area paziente", sub: "Consulta appuntamenti, note dei trattamenti e moduli di consenso.", email: "Email", send: "Inviami un codice di accesso",
    code: "Codice a 6 cifre", verify: "Accedi", usePw: "Usa la password", useCode: "Usa il codice via email", password: "Password",
    upcoming: "Prossimi", passport: "Passaporto", past: "Passati", messages: "Messaggi", forms: "Moduli", account: "Account", none: "Ancora nulla", notes: "Note della clinica",
    sign: "Firma ora", setPw: "Imposta una password (facoltativo)", save: "Salva", install: "Installa app", iosHint: "Su iPhone: tocca Condividi, poi 'Aggiungi a Home'.",
    book: "Prenota un nuovo appuntamento", logout: "Esci", codeSent: "Ti abbiamo inviato un codice via email (se registrata presso la clinica).",
    create: "Crea account", haveAccount: "Hai già un account? Accedi", noAccount: "Nuovo paziente? Crea account", pwHint: "Password (min. 8 caratteri)", firstName: "Nome", lastName: "Cognome", phone: "Telefono" },
};

function usePwa(slug) {
  const [prompt, setPrompt] = useState(null);
  useEffect(() => {
    const link = document.createElement("link");
    link.rel = "manifest"; link.href = `${process.env.REACT_APP_BACKEND_URL}/api/portal/${slug}/manifest.webmanifest`;
    const meta = document.createElement("meta");
    meta.name = "apple-mobile-web-app-capable"; meta.content = "yes";
    document.head.append(link, meta);
    if ("serviceWorker" in navigator) navigator.serviceWorker.register("/sw.js").catch(() => {});
    const h = (e) => { e.preventDefault(); setPrompt(e); };
    window.addEventListener("beforeinstallprompt", h);
    return () => { link.remove(); meta.remove(); window.removeEventListener("beforeinstallprompt", h); };
  }, [slug]);
  return prompt;
}

export function LoginBox({ slug, l, onIn }) {
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [pw, setPw] = useState("");
  const [mode, setMode] = useState("code");
  const [sent, setSent] = useState(false);
  const run = async (fn) => { try { await fn(); } catch (e) { toast.error(errMsg(e)); } };
  const login = (path, body) => run(async () => { const { data } = await api.post(`/portal/${slug}/${path}`, body); onIn(data); });
  return (
    <div className="rounded-[var(--radius)] border bg-card p-6 space-y-4 max-w-md" data-testid="portal-login">
      <Input data-testid="portal-email-input" type="email" placeholder={l.email} value={email} onChange={(e) => setEmail(e.target.value)} />
      {mode === "code" ? (
        <>
          {!sent ? (
            <Button data-testid="portal-send-code-btn" className="w-full rounded-full" disabled={!email} onClick={() => run(async () => { await api.post(`/portal/${slug}/request-code`, { email }); setSent(true); toast.success(l.codeSent); })}>{l.send}</Button>
          ) : (
            <>
              <Input data-testid="portal-code-input" inputMode="numeric" maxLength={6} placeholder={l.code} value={code} onChange={(e) => setCode(e.target.value)} className="tracking-[0.5em] text-center text-lg" />
              <Button data-testid="portal-verify-btn" className="w-full rounded-full" disabled={code.length !== 6} onClick={() => login("verify-code", { email, code })}>{l.verify}</Button>
            </>
          )}
        </>
      ) : (
        <>
          <Input data-testid="portal-password-input" type="password" placeholder={l.password} value={pw} onChange={(e) => setPw(e.target.value)} />
          <Button data-testid="portal-password-login-btn" className="w-full rounded-full" disabled={!email || !pw} onClick={() => login("login", { email, password: pw })}>{l.verify}</Button>
        </>
      )}
      <button data-testid="portal-toggle-mode" className="text-sm text-primary" onClick={() => setMode(mode === "code" ? "pw" : "code")}>{mode === "code" ? l.usePw : l.useCode}</button>
    </div>
  );
}

function RegisterBox({ slug, l, lang, onIn, onBack }) {
  const [f, setF] = useState({ first_name: "", last_name: "", email: "", phone: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState("");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { const { data } = await api.post(`/portal/${slug}/register`, { ...f, preferred_language: lang }); if (data.pending) setPending(data.message); else onIn(data); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  if (pending) return (
    <div className="rounded-[var(--radius)] border border-primary/30 bg-primary/5 p-6 space-y-3 max-w-md" data-testid="register-pending">
      <p className="font-semibold">Thank you for registering!</p><p className="text-sm text-muted-foreground">{pending}</p>
      <button type="button" data-testid="register-pending-back" className="text-sm font-semibold text-primary" onClick={onBack}>{l.haveAccount}</button>
    </div>
  );
  return (
    <form onSubmit={submit} className="rounded-[var(--radius)] border bg-card p-6 space-y-3 max-w-md" data-testid="portal-register">
      <div className="grid grid-cols-2 gap-3">
        <Input data-testid="register-first-name" required placeholder={l.firstName || "First name"} value={f.first_name} onChange={set("first_name")} />
        <Input data-testid="register-last-name" placeholder={l.lastName || "Last name"} value={f.last_name} onChange={set("last_name")} />
      </div>
      <Input data-testid="register-email" type="email" required placeholder={l.email} value={f.email} onChange={set("email")} />
      <Input data-testid="register-phone" type="tel" placeholder={l.phone || "Phone"} value={f.phone} onChange={set("phone")} />
      <label className="block text-xs text-muted-foreground">Date of birth (for your birthday treat)<Input data-testid="register-dob" type="date" value={f.dob || ""} onChange={set("dob")} /></label>
      <Input data-testid="register-password" type="password" required minLength={8} placeholder={l.pwHint} value={f.password} onChange={set("password")} />
      <Button data-testid="register-submit-btn" className="w-full rounded-full" disabled={busy}>{l.create}</Button>
      <button type="button" data-testid="register-to-login" className="text-base font-semibold text-primary" onClick={onBack}>{l.haveAccount}</button>
    </form>
  );
}

function ApptList({ items, l, lang }) {
  if (!items.length) return <p className="text-sm text-muted-foreground py-6">{l.none}</p>;
  return items.map((a) => (
    <div key={a.id} className="rounded-[var(--radius)] border bg-card p-5 space-y-2" data-testid={`portal-appt-${a.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="font-display text-lg font-bold">{a.date} · {a.start_time}</div><StatusBadge status={a.status} />
      </div>
      <div className="text-sm">{a.service_name} · {a.professional_name} · {a.location_name}</div>
      <div className="text-xs text-muted-foreground">{money(a.price, a.currency, lang)}</div>
      {a.notes && <div className="rounded-md bg-muted/50 p-3 text-sm whitespace-pre-wrap" data-testid={`portal-notes-${a.id}`}><div className="text-xs font-semibold mb-1">{l.notes}</div>{a.notes}</div>}
      {a.manage_token && ["confirmed", "pending_deposit"].includes(a.status) && <a className="inline-flex items-center text-xs text-primary" href={`/manage/${a.manage_token}`}>Manage <ChevronRight className="h-3 w-3" /></a>}
    </div>
  ));
}

export default function Portal() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const [register, setRegister] = useState(params.get("mode") === "register");
  const { lang } = useT();
  const l = L[lang] || L.en;
  const [info, setInfo] = useState(null);
  const [me, setMe] = useState(undefined);
  const [tab, setTab] = useState(params.get("tab") || "upcoming");
  const [appts, setAppts] = useState([]);
  const [forms, setForms] = useState([]);
  const [view, setView] = useState(null);
  const [pw, setPw] = useState("");
  const prompt = usePwa(slug);
  usePublicBranding(info);

  useEffect(() => {
    api.get(`/portal/${slug}/info`).then((r) => setInfo(r.data)).catch(() => setInfo(false));
    api.get(`/portal/${slug}/me`).then((r) => setMe(r.data)).catch(() => setMe(null));
  }, [slug]);
  const loadData = useCallback(() => {
    api.get(`/portal/${slug}/appointments`).then((r) => setAppts(r.data));
    api.get(`/portal/${slug}/consents`).then((r) => setForms(r.data));
  }, [slug]);
  useEffect(() => { if (me) loadData(); }, [me, loadData]);

  if (info === false) return <PublicFrame><p data-testid="portal-not-found">Clinic not found.</p></PublicFrame>;
  if (!info || me === undefined) return <PublicFrame><p>…</p></PublicFrame>;
  const today = new Date().toISOString().slice(0, 10);
  const upcoming = appts.filter((a) => a.date >= today && a.status !== "cancelled").reverse();
  const past = appts.filter((a) => a.date < today || a.status === "cancelled");
  const TABS = [["upcoming", CalendarDays], ["passport", HeartPulse], ["past", CalendarDays], ["forms", FileSignature], ["health", Stethoscope], ["packages", Package], ["invoices", Receipt], ["messages", MessageCircle], ["account", User]];

  return (
    <PublicFrame branding={info.branding}>
      <IosInstallTip slug={slug} name={info.branding?.clinic_name || info.name} lang={lang} />
      {!me ? (
        <div className="space-y-6">
          <div><h1 className="text-3xl sm:text-4xl font-bold">{l.title}</h1><p className="text-muted-foreground mt-2">{l.sub}</p></div>
          {register ? <RegisterBox slug={slug} l={l} lang={lang} onIn={setMe} onBack={() => setRegister(false)} /> : (
            <>
              <LoginBox slug={slug} l={l} onIn={setMe} />
              <button data-testid="portal-to-register" className="text-lg font-semibold text-primary hover:underline" onClick={() => setRegister(true)}>{l.noAccount}</button>
            </>
          )}
        </div>
      ) : (
        <div className="space-y-6" data-testid="portal-home">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <h1 className="text-3xl font-bold" data-testid="portal-greeting">{me.first_name} {me.last_name}</h1>
            <a href={`/book/${slug}`}><Button className="rounded-full" data-testid="portal-book-btn">{l.book}</Button></a>
          </div>
          <div className="flex gap-1 rounded-full border p-1 w-fit" data-testid="portal-tabs">
            {TABS.map(([k, Icon]) => (
              <button key={k} data-testid={`portal-tab-${k}`} onClick={() => setTab(k)} className={`flex items-center gap-1.5 rounded-full px-3 sm:px-4 py-1.5 text-sm ${tab === k ? "bg-primary text-primary-foreground" : ""}`}>
                <Icon className="h-3.5 w-3.5" /><span className="hidden sm:inline">{l[k] || L.en[k]}</span>
              </button>
            ))}
          </div>
          <div className="space-y-3">
            {tab === "upcoming" && <><LoyaltyCard slug={slug} /><ApptList items={upcoming} l={l} lang={lang} /></>}
            {tab === "passport" && <PassportTab slug={slug} />}
            {tab === "health" && <HealthTab slug={slug} />}
            {tab === "packages" && <PackagesTab slug={slug} />}
            {tab === "invoices" && <InvoicesTab slug={slug} />}
            {tab === "messages" && <div className="space-y-3" data-testid="portal-messages"><ContactButtons contact={info.branding?.contact} testPrefix="portal-contact" /><MessageThread url={`/portal/${slug}/messages`} self="patient" testId="portal-thread" placeholder="Write to the clinic…" /></div>}
            {tab === "past" && <ApptList items={past} l={l} lang={lang} />}
            {tab === "forms" && (forms.length ? forms.map((f) => (
              <div key={f.id} className="rounded-[var(--radius)] border bg-card p-4 flex items-center gap-3" data-testid={`portal-form-${f.id}`}>
                <FileSignature className="h-4 w-4 text-primary" /><div className="flex-1"><div className="font-medium">{f.template_name}</div>
                  <div className="text-xs text-muted-foreground">{(f.signed_at || f.created_at).slice(0, 10)}</div></div>
                {f.status === "signed" ? <Button size="sm" variant="ghost" data-testid={`portal-view-form-${f.id}`} onClick={() => api.get(`/portal/${slug}/consents/${f.id}`).then((r) => setView(r.data))}>View</Button>
                  : <a href={`/consent/${f.token}`}><Button size="sm" data-testid={`portal-sign-form-${f.id}`}>{l.sign}</Button></a>}
              </div>)) : <p className="text-sm text-muted-foreground py-6">{l.none}</p>)}
            {tab === "account" && (
              <div className="rounded-[var(--radius)] border bg-card p-6 space-y-4 max-w-md">
                <div className="text-sm">{me.email}<br />{me.phone}</div>
                <p className="text-sm font-medium">{l.setPw}</p>
                <div className="flex gap-2">
                  <Input type="password" minLength={8} data-testid="portal-set-password-input" value={pw} onChange={(e) => setPw(e.target.value)} />
                  <Button data-testid="portal-set-password-btn" disabled={pw.length < 8} onClick={() => api.post(`/portal/${slug}/set-password`, { password: pw }).then(() => { toast.success("OK"); setPw(""); }).catch((e) => toast.error(errMsg(e)))}>{l.save}</Button>
                </div>
                <Button variant="ghost" data-testid="portal-logout-btn" onClick={() => api.post(`/portal/${slug}/logout`).then(() => setMe(null))}><LogOut className="h-4 w-4 mr-1.5" />{l.logout}</Button>
              </div>
            )}
          </div>
        </div>
      )}
      <div className="mt-10 rounded-[var(--radius)] border border-dashed p-4 text-sm text-muted-foreground flex flex-wrap items-center gap-3" data-testid="portal-install-box">
        <Download className="h-4 w-4 text-primary" />
        {prompt ? <Button size="sm" variant="outline" data-testid="portal-install-btn" onClick={() => prompt.prompt()}>{l.install}</Button> : <span>{l.iosHint}</span>}
      </div>
      <Dialog open={!!view} onOpenChange={(o) => !o && setView(null)}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="portal-form-viewer">
          <DialogHeader><DialogTitle>{view?.content?.title || view?.template_name}</DialogTitle></DialogHeader>
          <p className="whitespace-pre-wrap text-sm text-muted-foreground">{view?.content?.body}</p>
          {view?.signature && <img src={view.signature} alt="signature" className="h-28 border rounded-md bg-white" />}
          <p className="text-xs text-muted-foreground">{view?.signer_name} · {view?.signed_at && new Date(view.signed_at).toLocaleString()}</p>
        </DialogContent>
      </Dialog>
    </PublicFrame>
  );
}
